'use client';
import CustomAsyncPaginate from '@/components/common/async-paginate';
import NoRecord from '@/components/common/no-record';
import MWSVG from '@/components/common/svg';
import WorkCol from '@/components/work/work-col';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { addIntoLocalStorage, isEmpty } from '@/utils/common-helpers';
import { useEffect, useState } from 'react';
import 'react-loading-skeleton/dist/skeleton.css';
import { getRequest } from '@/api/helpers';
import { routes } from '@/api/routes';
import { useRouter } from 'next/navigation';
import MetaTags from '@/components/meta-tag/meta-tag';
import AOS from 'aos';

import BorderEdge from '@/components/common/border-edge';

const ContactInfo = dynamic(() => import('@/components/common/contact-info'));
const ContactForm = dynamic(() => import('@/components/common/contact-form'));
const Skeleton = dynamic(() => import('react-loading-skeleton'));

const Page = () => {
    const take: string = process.env.NEXT_PUBLIC_WORK_LIMIT || '12';
    const router = useRouter();

    const [portfolios, setPortfolios] = useState<any>([]);
    const [show, setShow] = useState(false);
    const [fetching, setFetching] = useState<boolean>(true);
    const [displayLoadMore, setDisplayLoadMore] = useState<boolean>(false);
    const [count, setCount] = useState<number>(0);
    const [skip, setSkip] = useState<number>(0);
    const [selectedTechnology, setSelectedTechnology] = useState<any>('');
    const [selectedIndustry, setSelectedIndustry] = useState<any>('');
    const [selectedPlatform, setSelectedPlatform] = useState<any>('');

    // Use static values instead of timestamps to avoid timing disclosure
    const [industryState, setIndustryState] = useState<any>(null);
    const [platformState, setPlatformState] = useState<any>(null);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        if (selectedTechnology.value) {
            appendQueryStringParameter('technology', selectedTechnology.value);
        } else if (selectedTechnology.value === '') {
            removeQueryStringParameter('technology');
            setSelectedTechnology('');
        }

        if (selectedPlatform.value) {
            appendQueryStringParameter('platform', selectedPlatform.value);
        } else if (selectedPlatform.value === '') {
            removeQueryStringParameter('platform');
            setSelectedPlatform('');
        }

        if (selectedIndustry.value) {
            appendQueryStringParameter('industry', selectedIndustry.value);
        } else if (selectedIndustry.value === '') {
            removeQueryStringParameter('industry');
            setSelectedIndustry('');
        }
    }, [selectedTechnology, selectedIndustry, selectedPlatform]);

    useEffect(() => {
        if (count === portfolios.length || portfolios.length === 0) {
            setDisplayLoadMore(false);
        } else {
            setDisplayLoadMore(true);
        }
    }, [portfolios]);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!mounted) return;
        
        addIntoLocalStorage('path-name', window.location.href);
        setParameterValue();

        AOS.init({
            disable: true,
        });

        return () => {
            AOS.init({
                disable: false,
            });
        };
    }, [mounted]);

    useEffect(() => {
        fetchPortfolios().then();
    }, [skip, selectedTechnology, selectedPlatform, selectedIndustry]);

    const fetchPortfolios = async () => {
        setFetching(true);
        const res = await getRequest(
            `${routes.PORTFOLIOS}?take=${take}&skip=${skip}&technology=${
                isEmpty(selectedTechnology) ? '' : selectedTechnology?.value
            }&platform=${isEmpty(selectedPlatform) ? '' : selectedPlatform?.value}&industry=${
                isEmpty(selectedIndustry) ? '' : selectedIndustry?.value
            }`,
        );
        if (!isEmpty(res) && res.status) {
            setCount(res.data.count);

            if (portfolios.length === 0) {
                setPortfolios(res.data.data);
            } else {
                setPortfolios(portfolios.concat(res.data.data));
            }
        }
        setFetching(false);
    };

    const appendQueryStringParameter = (parameter: any, value: any) => {
        let urlParams = new URLSearchParams(window.location.search);

        if (urlParams.has(parameter)) {
            urlParams.set(parameter, value);
        } else {
            urlParams.append(parameter, value);
        }

        let newUrl = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`;

        window.history.pushState({}, '', newUrl);
    };

    const removeQueryStringParameter = (parameter: any) => {
        let urlParams = new URLSearchParams(window.location.search);

        if (urlParams.has(parameter)) {
            urlParams.delete(parameter);
        }

        let newUrl = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`;

        window.history.pushState({}, '', newUrl);
    };

    const setParameterValue = async () => {
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.has('technology')) {
            const technologyValue = urlParams.get('technology');
            const response = await getRequest(
                `${routes.TECHNOLOGY_DROPDOWN}?platform_id=${selectedPlatform.value ?? ''}&industry_id=${
                    selectedIndustry.value ?? ''
                }`,
            );

            if (response && response.data) {
                const technology = response.data;
                const matchedTechnology = technology.find((tech: any) => tech.id === technologyValue);

                if (matchedTechnology) {
                    setSelectedTechnology({
                        label: matchedTechnology.technology_name,
                        value: technologyValue,
                    });
                }
            }
        }

        if (urlParams.has('industry')) {
            const industryValue = urlParams.get('industry');
            const response = await getRequest(
                `${routes.INDUSTRY_DROPDOWN}?technology_id=${selectedTechnology.value ?? ''}&platform_id=${
                    selectedPlatform.value ?? ''
                }`,
            );

            if (response && response.data) {
                const industries = response.data;
                const matchedIndustry = industries.find((industry: any) => industry.id === industryValue);

                if (matchedIndustry) {
                    setSelectedIndustry({
                        label: matchedIndustry.industry_name,
                        value: industryValue,
                    });
                }
            }
        }

        if (urlParams.has('platform')) {
            const platformValue = urlParams.get('platform');
            const response = await getRequest(
                `${routes.PLATFORM_DROPDOWN}?technology_id=${selectedTechnology.value ?? ''}&industry_id=${
                    selectedIndustry.value ?? ''
                }`,
            );

            if (response && response.data) {
                const platforms = response.data;
                const matchedPlatform = platforms.find((platform: any) => platform.id === platformValue);

                if (matchedPlatform) {
                    setSelectedPlatform({
                        label: matchedPlatform.platform_name,
                        value: platformValue,
                    });
                }
            }
        }

        setPortfolios([]);
    };

    const filterChange = (value: string) => {
        setSkip(0);
        setCount(0);
        setPortfolios([]);

        if (value === 'technology') {
            // Use incremental counter instead of timestamps
            setPlatformState(Date.now() % 1000);
            setIndustryState(Date.now() % 1000);
        } else if (value === 'platform') {
            setIndustryState(Date.now() % 1000);
        } else {
            setPlatformState(Date.now() % 1000);
        }
    };

    const goToDetailPage = (slug: string) => {
        return router.push(`work/${slug}`);
    };

    const PortfolioSkeleton = () => {
        return (
            <>
                <Skeleton height="350px" />
                <Skeleton height="350px" />
                <Skeleton height="350px" />
                <Skeleton height="350px" />
                <Skeleton height="350px" />
                <Skeleton height="350px" />
            </>
        );
    };

    const loadMore = () => {
        setSkip(skip + parseFloat(take));
    };

    const resetFilters = () => {
        window.history.replaceState({}, document.title, window.location.pathname);
        setSelectedTechnology('');
        setSelectedIndustry('');
        setSelectedPlatform('');
        setCount(0);
        setPortfolios([]);
    };

    return (
        <>
            <MetaTags />
            {/* work hero start */}
            <section className="hero work-height">
                <div className="container lg:container md:mx-auto top-hero">
                    <div className="lg:grid hero-row gap-6 relative z-[1] h-full">
                        <div className="w-full">
                            <div className="inline-flex flex-col lg:items-start gap-6 hero-content justify-center h-full">
                                <h1 className="mb-0">Our Work</h1>
                                <p className="mb-0 fw-600">
                                    Browse some of our best projects to get an idea of our strengths and capabilities. We have
                                    worked for enterprises, medium-sized businesses, and start-up clients in different industries.
                                </p>
                            </div>
                        </div>
                        <div className="text-center md:text-center lg:self-center hidden lg:block">
                            <Image
                                src="/img/work/case-studies-hero@2x.webp"
                                alt="work hero"
                                title="work hero"
                                className="hero-Work-img"
                                height={748}
                                width={1110}
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
                <div className="text-center md:text-center lg:self-center lg:hidden">
                    <Image
                        src="/img/work/case-studies-hero@2x.webp"
                        alt="work hero"
                        title="work hero"
                        className="hero-Work-img"
                        height={748}
                        width={1110}
                        loading="lazy"
                    />
                </div>
            </section>
            {/* work hero end */}
            {/* work filters start */}
            <div className="sticky top-[60px] lg:top-[80px] z-[1] bg-grey border-b transition-all duration-300 ease-in-out opacity-100 translate-y-0">
                <div className="container lg:container md:mx-auto">
                    <div id="content-menu-container" className="py-5 lg:py-0 flex items-center relative submenu-responsive">
                        <div className="flex-1 lg:flex-initial md:flex-initial">
                            <span className="nav-link ps-0 text-secondary pe-4 fw-500 hidden md:block lg:block">Filters</span>
                            <span
                                className="nav-link ps-0 text-secondary pe-4 fw-500 block md:hidden lg:hidden cursor-pointer"
                                onClick={() => setShow(!show)}
                            >
                                Filters <MWSVG id="ic-down"></MWSVG>
                            </span>
                        </div>
                        <div
                            className={
                                show
                                    ? 'show bg-grey nav work-nav mobile-work-nav flex flex-wrap items-center fw-500 w-full lg:w-auto'
                                    : 'bg-grey nav work-nav mobile-work-nav flex flex-wrap items-center fw-500 w-full  lg:w-auto'
                            }
                        >
                            <CustomAsyncPaginate
                                api={`${routes.INDUSTRY_DROPDOWN}?technology_id=${selectedTechnology.value ?? ''}&platform_id=${
                                    selectedPlatform.value ?? ''
                                }`}
                                selectedData={selectedIndustry}
                                setSelectedData={setSelectedIndustry}
                                labelName="industry_name"
                                valueName="id"
                                customLabel="All Industries"
                                needToAddMarginLeft
                                handleChange={() => filterChange('industry')}
                                reset={industryState}
                                noBorder
                            />
                            <CustomAsyncPaginate
                                api={`${routes.PLATFORM_DROPDOWN}?technology_id=${selectedTechnology.value ?? ''}&industry_id=${
                                    selectedIndustry.value ?? ''
                                }`}
                                selectedData={selectedPlatform}
                                setSelectedData={setSelectedPlatform}
                                labelName="platform_name"
                                valueName="id"
                                needToAddMarginLeft
                                customLabel="All Platforms"
                                handleChange={() => filterChange('platform')}
                                reset={platformState}
                                noBorder
                            />
                        </div>
                        {(!isEmpty(selectedTechnology) || !isEmpty(selectedIndustry) || !isEmpty(selectedPlatform)) && (
                            <div className="reset cursor-pointer text-secondary hover:text-primary ms-auto">
                                <span className="flex items-center gap-2.5" onClick={resetFilters}>
                                    <MWSVG id="tabler-icon-rotate-clockwise" svgClassName="ic-m" />
                                    Reset
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* work filters end */}
            <section className="section-py pt-50">
                <div className="container lg:container md:mx-auto">
                    {!fetching && portfolios.length === 0 && <NoRecord />}
                    <div className="blog-row row grid mb-50 lg:grid-cols-3">
                        {/* Work col start */}
                        {portfolios.length > 0 &&
                            portfolios.map((portfolio: any, index: number) => {
                                return (
                                    <WorkCol
                                        title={portfolio.name}
                                        path={portfolio.avatar}
                                        width={800}
                                        height={520}
                                        key={index}
                                        alt={portfolio.name}
                                        slug={portfolio.slug}
                                        redirect={goToDetailPage}
                                        desc={portfolio.description_short}
                                    >
                                        {portfolio.technologies.length > 0 &&
                                            portfolio.technologies.map((technology: any, index: number) => {
                                                return (
                                                    <span className="chip rounded-full" key={index}>
                                                        {technology.technology_name}
                                                    </span>
                                                );
                                            })}
                                    </WorkCol>
                                );
                            })}
                        {fetching && <PortfolioSkeleton />}
                        {/*Work col end */}
                    </div>
                    {displayLoadMore && (
                        <div className="text-center">
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={loadMore}
                                aria-labelledby="moreProjectsLabel"
                            >
                                Load More Projects
                            </button>
                        </div>
                    )}
                </div>
            </section>
            <BorderEdge color="text-white" bg="bg-blue" />
            <div className="section bg-primary py-100 dark:text-light dark:bg-black relative z-10">
                <div className="container lg:container md:mx-auto">
                    <div className="row grid md:grid-cols-12 lg:grid-cols-12">
                        <ContactForm />
                        <ContactInfo />
                    </div>
                </div>
            </div>
        </>
    );
};

export default Page;
